import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { FrequentTimeZones, isCoach, isEdgeCoach, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { createFileRoute, Link } from '@tanstack/react-router';
import { paymentsTabs } from './-payments-tabs';
import { EntityTable } from '@/components/table/entity-table';
import { formatCurrency } from '@/lib/common-utils';
import { coachPaymentKind, CoachPaymentReport, PayCycleDTO } from '@airreading/common/data';
import { useReactTable, getCoreRowModel, createColumnHelper } from '@tanstack/react-table';
import { CoachPaymentKindTag } from '../teachers/-components/payments/coach-payment-kind-tag';
import { getCoachPaymentAsCoach, getMeCoach, getTotalSumFromLifetimeAsCoach } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { paginationRequestSchema } from '@/lib/pagination';
import { z } from 'zod';
import { usePaginatedFilters } from '@/lib/filtering';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Badge } from '@/components/ui/badge';
import { Temporal } from 'temporal-polyfill';

export const Route = createFileRoute('/_layout/payments/')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  component: Page,
  validateSearch: paginationRequestSchema.extend({
    startOnOrAfterPlainDate: z.string().optional(),
    startOnOrBeforePlainDate: z.string().optional(),
  }),
});

const columnHelper = createColumnHelper<CoachPaymentReport>();

const coachPaymentReportTableColumns = (currentPaydate: Temporal.PlainDate) => [
  columnHelper.accessor('payDate', {
    id: 'paymentDate',
    header: 'Pay initiation date',
    cell: ({ getValue, row }) => {
      const isCurrent =
        currentPaydate &&
        PayCycleDTO.isWithinWindow(
          Temporal.PlainDate.from(currentPaydate),
          PayCycleDTO.fromPayPeriodId(row.original.payCycleId),
        );

      return (
        <div className="flex items-center gap-2">
          <span>{getValue()}</span>
          {isCurrent && (
            <Badge variant="outline" className="text-orange-600 bg-[#FFF6D4] border-[#FFF6D4] font-semibold">
              Current payment
            </Badge>
          )}
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('depositPostedDate', {
    id: 'depositPostedDate',
    header: 'Delivery (1-3 business days)',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('payPeriodInterval', {
    id: 'payPeriod',
    header: 'Period',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('type', {
    id: 'paymentType',
    header: 'Type',
    cell: ({ getValue }) => {
      return <CoachPaymentKindTag kind={getValue()} />;
    },
    enableSorting: false,
  }),

  columnHelper.accessor((row) => row, {
    id: 'amount',
    header: 'Gross/net earnings',
    cell: ({ getValue }) => {
      const row = getValue();

      const { totalAmountInCents, isLatestTeachingPayment } = row;

      const amount = formatCurrency(totalAmountInCents / 100);

      const isTeachingPayment = row.type === coachPaymentKind.enum.teaching;

      if (isTeachingPayment && isLatestTeachingPayment) {
        return (
          <Link
            to={'/payments/teaching/$payCycleId'}
            params={{ payCycleId: row.payCycleId }}
            search={{
              pageIndex: 0,
              pageSize: 20,
            }}
            className="text-blue-600 underline underline-offset-2"
          >
            {amount}
          </Link>
        );
      }

      return amount;
    },
    enableSorting: false,
  }),
];

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load coach payments. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <CoachPaymentsPage />
    </InfoPageSuspenseBoundary>
  );
}

function CoachPaymentsPage() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    resetFilters,
    filters: {
      startOnOrAfterPlainDate: [[rawStartOnOrAfterPlainDate], setStartOnOrAfterPlainDate],
      startOnOrBeforePlainDate: [[rawStartOnOrBeforePlainDate], setStartOnOrBeforePlainDate],
    },
  } = usePaginatedFilters(Route.id, ['startOnOrAfterPlainDate', 'startOnOrBeforePlainDate']);

  const { data: me } = useQuery(getMeCoach());

  const startOnOrAfterPlainDate = plainDateFromStringOrElseUndefined(rawStartOnOrAfterPlainDate);
  const startOnOrBeforePlainDate = plainDateFromStringOrElseUndefined(rawStartOnOrBeforePlainDate);

  const { data, isPending, refetch, error } = useQuery(
    getCoachPaymentAsCoach({
      pageIndex,
      pageSize,
      filters: {
        payDateStartOnOrAfterPlainDate: startOnOrAfterPlainDate,
        payDateStartOnOrBeforePlainDate: startOnOrBeforePlainDate,
      },
    }),
  );

  const { data: totalSum } = useQuery(
    getTotalSumFromLifetimeAsCoach({
      filters: {
        payDateStartOnOrAfterPlainDate: startOnOrAfterPlainDate,
        payDateStartOnOrBeforePlainDate: startOnOrBeforePlainDate,
      },
    }),
  );

  const currentPaydate = PayCycleDTO.paycycleWindow(
    Temporal.Now.plainDateISO(me?.timeZone ?? FrequentTimeZones.UsCentral),
  );

  const table = useReactTable({
    data: data?.payments ?? [],
    columns: coachPaymentReportTableColumns(currentPaydate.paymentPlainDate),
    getRowId: (row) => row.coach.id + row.type + row.payCycleId,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
    onPaginationChange,
  });

  const filterLeftComponents = (
    <>
      <TableFilterPlainDateRangePicker
        fromPlainDateString={rawStartOnOrAfterPlainDate}
        toPlainDateString={rawStartOnOrBeforePlainDate}
        onRangeSelect={(range) => {
          setStartOnOrAfterPlainDate(range.fromPlainDateString);
          setStartOnOrBeforePlainDate(range.toPlainDateString);
        }}
        placeholder="Date"
      />
      <TableFilterResetButton show onClick={resetFilters} />
    </>
  );

  return (
    <div>
      <InfoPage headerText="Payments" tabs={paymentsTabs('Past payments')}>
        <EntityTable
          table={table}
          showPagination
          filters={{ filterLeftComponents }}
          topBarBottomComponents={
            <p className="text-sm font-semibold">
              Sum <span className="font-bold text-2xl">{formatCurrency((totalSum ?? 0) / 100)}</span>
            </p>
          }
          isLoading={isPending}
          hasError={!!error}
          emptyState="No payments found"
          errorState={<TableErrorState refetchAction={refetch} />}
        />
      </InfoPage>
    </div>
  );
}
